import {useFluxStore} from 'client/framework';
import React, {useEffect, useState} from 'react';
import {Helmet} from 'react-helmet';
import {Button, ButtonGroup, LoadingSpinner} from '@albert-io/atomic';
import {title as titleTag, meta} from 'lib/head';

import {history} from 'client/history';
import sessionStore from 'client/Session/SessionStore';
import UpgradeSubjectModal from 'components/PracticeView/UpgradeSubjectModal/UpgradeSubjectModal.react';

import type {SubjectFaQsQuery} from './SubjectFAQList.queries.generated';
import {useSubjectFaQsQuery} from './SubjectFAQList.queries.generated';
import SubjectFAQItem from './SubjectFAQItem/SubjectFAQItem';
import SubjectFAQQuestions from './SubjectFAQQuestions/SubjectFAQQuestions';

type GqlSubject = NonNullable<NonNullable<SubjectFaQsQuery['subjects']>['results']>[number];

/**
 * Adapter that wraps GraphQL subject data to satisfy the SubjectModelV2
 * API surface that UpgradeSubjectModal expects.
 */
function createSubjectAdapter(subject: GqlSubject) {
  return {
    getId: () => subject.id,
    get: (key: string) => {
      if (key === 'name') return subject.name;
      return undefined;
    },
    getUrlSlug: () => subject.urlSlug,
    doesUserHaveAccess: () => subject.userHasAccess ?? false,
    getSubscriptionsPrices: () => ({
      first: () => subject.subscriptionsPrices?.[0] ?? null
    }),
    getMeta: () => ({
      getCountOfQuestions: () => subject.countOfQuestions ?? 0,
      getCountOfFreeResponseQuestions: () => subject.countOfFreeResponseQuestions ?? 0,
      isUserHasAccess: () => subject.userHasAccess ?? false
    })
  };
}

interface SubjectFAQListProps {
  params: {
    slug: string;
  };
}

const SubjectFAQList = ({params}: SubjectFAQListProps) => {
  const hasCMAccess = useFluxStore(sessionStore, (s) => s.hasContentManagerAccess());
  const isLicensedStudent = useFluxStore(sessionStore, (s) => s.hasValidSession() && s.isStudent());
  const {slug} = params;
  const {data, loading, error} = useSubjectFaQsQuery({variables: {slug}});

  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [isRearrangeMode, setIsRearrangeMode] = useState(false);
  const [showUpgradeSubjectModal, setShowUpgradeSubjectModal] = useState(false);

  const subject = data?.subjects?.results?.[0];
  const subjectFaqs = subject?.subjectFaqs ?? [];

  useEffect(() => {
    if (loading) return;
    // Query failed or no subject found (invalid slug) — redirect everyone
    if (error || !subject) {
      history.replace(`/${slug}`, null);
      return;
    }
    // Not admin and no FAQs — redirect to subject page
    if (!hasCMAccess && subjectFaqs.length === 0) {
      history.replace(`/${slug}`, null);
    }
  }, [loading, error, subject, subjectFaqs.length, slug]);

  // Show the upgrade modal once the subject loads for a licensed-but-unentitled student,
  // adjusted during render
  const [prevSubjectSync, setPrevSubjectSync] = useState<{
    subject?: typeof subject;
    loading?: boolean;
  }>({});
  if (prevSubjectSync.subject !== subject || prevSubjectSync.loading !== loading) {
    setPrevSubjectSync({subject, loading});
    if (
      subject &&
      !loading &&
      isLicensedStudent &&
      subject.hasAccessViaLicenseAsStudent &&
      !subject.userHasAccess
    ) {
      setShowUpgradeSubjectModal(true);
    }
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!subject) {
    return null;
  }

  const metaTitle = `${subject.name} | Frequently Asked Questions | Albert`;
  const metaDescription = `Students often ask the same questions. Here are the most frequently asked questions for ${subject.name}.`;

  return (
    <>
      <Helmet>
        {titleTag(metaTitle)}
        {meta.description(metaDescription)}
      </Helmet>
      <SubjectFAQQuestions
        subjectFaqs={subjectFaqs}
        subjectId={subject.id}
        slug={slug}
        isRearrangeMode={isRearrangeMode}
        onSetRearrangeMode={setIsRearrangeMode}
      />
      {!isRearrangeMode && (
        <div>
          {subjectFaqs.map((subjectFAQ) => (
            <SubjectFAQItem
              key={subjectFAQ.id}
              subjectFAQ={subjectFAQ}
              subjectId={subject.id}
              slug={slug}
            />
          ))}
          {isCreatingNew && (
            <SubjectFAQItem
              subjectFAQ={{
                id: '',
                question: '',
                answer: '',
                orderPosition: subjectFaqs.length
              }}
              subjectId={subject.id}
              slug={slug}
              isCreatingNew
              onDiscardNew={() => setIsCreatingNew(false)}
            />
          )}
        </div>
      )}
      <ButtonGroup align='right'>
        {!isCreatingNew && !isRearrangeMode && hasCMAccess ? (
          <Button onClick={() => setIsCreatingNew(true)}>Add new FAQ</Button>
        ) : null}
      </ButtonGroup>
      {showUpgradeSubjectModal && (
        <UpgradeSubjectModal
          subject={createSubjectAdapter(subject)}
          handleClose={() => setShowUpgradeSubjectModal(false)}
        />
      )}
    </>
  );
};

export default SubjectFAQList;
